/**
 * INVOICE PDF GENERATION SERVICE
 * 
 * Professional PDF generation service for legal invoices
 * with company branding, proper formatting, and legal compliance.
 * Supports both SUN and Aureus company entities.
 */

import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

export interface InvoiceLineItem {
 description: string
 expense_category?: string | null
 quantity: number
 unit_price: number
 amount: number
 expense_date?: string | null
}

export interface InvoiceData {
 invoice_number: string
 invoice_date: string
 due_date: string | null
 company_entity: 'SUN' | 'Aureus'
 status: string
 subtotal: number
 tax_rate: number
 tax_amount: number
 total_amount: number
 currency: string
 description: string | null
 notes: string | null
 user: {
 full_name: string | null
 username: string
 email: string
 }
 line_items: InvoiceLineItem[]
}

// Company details for each entity
const COMPANY_DETAILS = {
 SUN: {
 name: 'SUN Alliance Holdings (Pty) Ltd',
 tagline: 'Non-Mining Operations Division',
 address: '1848 Mees Avenue, Randpark Ridge, Randburg, Gauteng, 2169',
 registration: '2025/368711/07',
 email: 'support@aureus.africa',
 website: 'aureus.africa',
 phone: '+27 74 449 3251',
 color: { r: 255, g: 193, b: 7 }, // Yellow/Gold for SUN
 bankDetails: {
 bankName: 'First National Bank (FNB)',
 accountName: 'SUN Alliance Holdings (Pty) Ltd',
 accountNumber: 'XXXXXXXXXX',
 branchCode: '250655',
 swiftCode: 'FIRNZAJJ',
 reference: 'Invoice number'
 }
 },
 Aureus: {
 name: 'Aureus Alliance Holdings (Pty) Ltd',
 tagline: 'Gold Mining Operations',
 address: '1848 Mees Avenue, Randpark Ridge, Randburg, Gauteng, 2169',
 registration: '2025/368711/07',
 email: 'support@aureus.africa',
 website: 'aureus.africa',
 phone: '+27 74 449 3251',
 color: { r: 212, g: 175, b: 55 }, // Gold for Aureus
 bankDetails: {
 bankName: 'First National Bank (FNB)',
 accountName: 'Aureus Alliance Holdings (Pty) Ltd',
 accountNumber: 'XXXXXXXXXX',
 branchCode: '250655',
 swiftCode: 'FIRNZAJJ',
 reference: 'Invoice number'
 }
 }
}

// Payment terms
const PAYMENT_TERMS = [
 'Payment is due within 30 days of invoice date unless otherwise specified.',
 'Please include the invoice number as payment reference.',
 'For international transfers, please include all applicable bank charges.',
 'All amounts are in the currency specified on this invoice.'
]

export class InvoicePdfService {
 private doc: jsPDF
 private pageHeight: number
 private pageWidth: number
 private margin: number = 20
 private currentY: number = 20
 private companyDetails: typeof COMPANY_DETAILS.SUN

 constructor() {
 this.doc = new jsPDF()
 this.pageHeight = this.doc.internal.pageSize.height
 this.pageWidth = this.doc.internal.pageSize.width
 this.companyDetails = COMPANY_DETAILS.SUN
 }

 /**
 * Generate professional PDF invoice
 */
 async generateInvoicePdf(invoice: InvoiceData): Promise<Blob> {
 console.log('Generating PDF invoice...')

 try {
 // Reset document
 this.doc = new jsPDF()
 this.currentY = 20
 this.companyDetails = COMPANY_DETAILS[invoice.company_entity]

 // Add company header
 this.addCompanyHeader(invoice.company_entity)

 // Add invoice title and number
 this.addInvoiceTitle(invoice)

 // Add billing details
 this.addBillingDetails(invoice)

 // Add line items table
 this.addLineItemsTable(invoice)

 // Add totals section
 this.addTotalsSection(invoice)

 // Add bank details section
 this.addBankDetailsSection(invoice)

 // Add notes section if applicable
 if (invoice.notes || invoice.description) {
 this.addNotesSection(invoice)
 }

 // Add payment terms
 this.addPaymentTerms()

 // Add footer with legal info
 this.addFooter(invoice)

 console.log('PDF invoice generated successfully')
 return this.doc.output('blob')

 } catch (error) {
 console.error('Error generating PDF invoice:', error)
 throw error
 }
 }

 /**
 * Add company header with logo and contact details
 */
 private addCompanyHeader(_entity: 'SUN' | 'Aureus') {
 const { color } = this.companyDetails
 
 // Company name
 this.doc.setFontSize(22)
 this.doc.setFont('helvetica', 'bold')
 this.doc.setTextColor(color.r, color.g, color.b)
 this.doc.text(this.companyDetails.name.toUpperCase(), this.margin, this.currentY)
 
 this.currentY += 8

 // Tagline
 this.doc.setFontSize(11)
 this.doc.setFont('helvetica', 'italic')
 this.doc.setTextColor(100, 100, 100)
 this.doc.text(this.companyDetails.tagline, this.margin, this.currentY)
 
 this.currentY += 15

 // Contact details on right side
 this.doc.setFontSize(9)
 this.doc.setFont('helvetica', 'normal')
 this.doc.setTextColor(80, 80, 80)

 const rightX = this.pageWidth - this.margin
 let contactY = 20
 this.doc.text(this.companyDetails.address, rightX, contactY, { align: 'right' })
 contactY += 5
 this.doc.text(`Reg: ${this.companyDetails.registration}`, rightX, contactY, { align: 'right' })
 contactY += 5
 this.doc.text(`Email: ${this.companyDetails.email}`, rightX, contactY, { align: 'right' })
 contactY += 5
 this.doc.text(`Phone: ${this.companyDetails.phone}`, rightX, contactY, { align: 'right' })

 // Add horizontal line
 this.doc.setDrawColor(color.r, color.g, color.b)
 this.doc.setLineWidth(1)
 this.doc.line(this.margin, this.currentY, this.pageWidth - this.margin, this.currentY)

 this.currentY += 15
 }

 /**
 * Add invoice title and number section
 */
 private addInvoiceTitle(invoice: InvoiceData) {
 // Large INVOICE title
 this.doc.setFontSize(28)
 this.doc.setFont('helvetica', 'bold')
 this.doc.setTextColor(50, 50, 50)
 this.doc.text('INVOICE', this.margin, this.currentY)

 // Invoice number on right
 this.doc.setFontSize(12)
 this.doc.setFont('helvetica', 'bold')
 this.doc.text(`Invoice #: ${invoice.invoice_number}`, this.pageWidth - this.margin, this.currentY - 5, { align: 'right' })

 // Status badge
 this.doc.setFontSize(10)
 this.doc.setFont('helvetica', 'normal')
 const statusText = invoice.status.toUpperCase().replace('_', ' ')
 const statusColor = this.getStatusColor(invoice.status)
 this.doc.setTextColor(statusColor.r, statusColor.g, statusColor.b)
 this.doc.text(`Status: ${statusText}`, this.pageWidth - this.margin, this.currentY + 3, { align: 'right' })

 this.currentY += 20
 }

 private getStatusColor(status: string): { r: number, g: number, b: number } {
 const colors: { [key: string]: { r: number, g: number, b: number } } = {
 'draft': { r: 107, g: 114, b: 128 },
 'pending_approval': { r: 245, g: 158, b: 11 },
 'approved': { r: 16, g: 185, b: 129 },
 'paid': { r: 34, g: 197, b: 94 },
 'rejected': { r: 239, g: 68, b: 68 },
 'voided': { r: 156, g: 163, b: 175 }
 }
 return colors[status] || { r: 100, g: 100, b: 100 }
 }

 /**
 * Add billing details section
 */
 private addBillingDetails(invoice: InvoiceData) {
 // Left column - Bill To
 this.doc.setFontSize(10)
 this.doc.setFont('helvetica', 'bold')
 this.doc.setTextColor(100, 100, 100)
 this.doc.text('BILL TO:', this.margin, this.currentY)

 this.currentY += 6
 this.doc.setFont('helvetica', 'normal')
 this.doc.setTextColor(50, 50, 50)
 this.doc.text(invoice.user.full_name || invoice.user.username, this.margin, this.currentY)
 this.currentY += 5
 this.doc.setTextColor(100, 100, 100)
 this.doc.text(invoice.user.email, this.margin, this.currentY)

 // Right column - Invoice Details
 let detailsY = this.currentY - 11
 const rightCol = this.pageWidth - this.margin - 60

 this.doc.setFontSize(10)
 this.doc.setFont('helvetica', 'bold')
 this.doc.setTextColor(100, 100, 100)
 this.doc.text('Invoice Date:', rightCol, detailsY)
 this.doc.setFont('helvetica', 'normal')
 this.doc.setTextColor(50, 50, 50)
 this.doc.text(this.formatDate(invoice.invoice_date), rightCol + 62, detailsY)

 detailsY += 6
 this.doc.setFont('helvetica', 'bold')
 this.doc.setTextColor(100, 100, 100)
 this.doc.text('Due Date:', rightCol, detailsY)
 this.doc.setFont('helvetica', 'normal')
 this.doc.setTextColor(50, 50, 50)
 this.doc.text(invoice.due_date ? this.formatDate(invoice.due_date) : 'On Receipt', rightCol + 62, detailsY)

 detailsY += 6
 this.doc.setFont('helvetica', 'bold')
 this.doc.setTextColor(100, 100, 100)
 this.doc.text('Currency:', rightCol, detailsY)
 this.doc.setFont('helvetica', 'normal')
 this.doc.setTextColor(50, 50, 50)
 this.doc.text(invoice.currency || 'USD', rightCol + 62, detailsY)

 this.currentY += 20
 }

 private formatDate(dateStr: string): string {
 const date = new Date(dateStr)
 return date.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })
 }

 /**
 * Add line items table
 */
 private addLineItemsTable(invoice: InvoiceData) {
 const { color } = this.companyDetails

 const tableData = invoice.line_items.map(item => [
 item.description,
 item.expense_category || '-',
 item.expense_date ? this.formatDate(item.expense_date) : '-',
 item.quantity.toString(),
 this.formatCurrency(item.unit_price, invoice.currency),
 this.formatCurrency(item.amount, invoice.currency)
 ])

 autoTable(this.doc, {
 startY: this.currentY,
 head: [['Description', 'Category', 'Date', 'Qty', 'Unit Price', 'Amount']],
 body: tableData,
 theme: 'striped',
 headStyles: {
 fillColor: [color.r, color.g, color.b],
 textColor: [255, 255, 255],
 fontStyle: 'bold',
 fontSize: 10
 },
 bodyStyles: {
 fontSize: 9,
 textColor: [50, 50, 50]
 },
 columnStyles: {
 0: { cellWidth: 55 },
 1: { cellWidth: 30 },
 2: { cellWidth: 25 },
 3: { cellWidth: 15, halign: 'center' },
 4: { cellWidth: 25, halign: 'right' },
 5: { cellWidth: 25, halign: 'right' }
 },
 margin: { left: this.margin, right: this.margin }
 })

 this.currentY = (this.doc as any).lastAutoTable.finalY + 10
 }

 private formatCurrency(amount: number, currency: string = 'USD'): string {
 return new Intl.NumberFormat('en-US', {
 style: 'currency',
 currency: currency,
 minimumFractionDigits: 2
 }).format(amount)
 }

 /**
 * Add totals section
 */
 private addTotalsSection(invoice: InvoiceData) {
 const rightCol = this.pageWidth - this.margin - 60
 const valueCol = this.pageWidth - this.margin

 // Subtotal
 this.doc.setFontSize(10)
 this.doc.setFont('helvetica', 'normal')
 this.doc.setTextColor(100, 100, 100)
 this.doc.text('Subtotal:', rightCol, this.currentY)
 this.doc.setTextColor(50, 50, 50)
 this.doc.text(this.formatCurrency(invoice.subtotal, invoice.currency), valueCol, this.currentY, { align: 'right' })

 this.currentY += 6

 // Tax
 if (invoice.tax_rate > 0) {
 this.doc.setTextColor(100, 100, 100)
 this.doc.text(`Tax (${invoice.tax_rate}%):`, rightCol, this.currentY)
 this.doc.setTextColor(50, 50, 50)
 this.doc.text(this.formatCurrency(invoice.tax_amount, invoice.currency), valueCol, this.currentY, { align: 'right' })
 this.currentY += 6
 }

 // Total line
 const { color } = this.companyDetails
 this.doc.setDrawColor(color.r, color.g, color.b)
 this.doc.setLineWidth(0.5)
 this.doc.line(rightCol - 10, this.currentY, valueCol, this.currentY)
 this.currentY += 6

 // Total
 this.doc.setFontSize(14)
 this.doc.setFont('helvetica', 'bold')
 this.doc.setTextColor(color.r, color.g, color.b)
 this.doc.text('TOTAL:', rightCol, this.currentY)
 this.doc.setTextColor(50, 50, 50)
 this.doc.text(this.formatCurrency(invoice.total_amount, invoice.currency), valueCol, this.currentY, { align: 'right' })

 this.currentY += 20
 }

 /**
 * Add bank details section
 */
 private addBankDetailsSection(invoice: InvoiceData) {
 const { color, bankDetails } = this.companyDetails

 // Check if we need a new page
 if (this.currentY > this.pageHeight - 100) {
 this.doc.addPage()
 this.currentY = 20
 }

 // Section header
 this.doc.setFontSize(11)
 this.doc.setFont('helvetica', 'bold')
 this.doc.setTextColor(color.r, color.g, color.b)
 this.doc.text('BANK DETAILS FOR PAYMENT', this.margin, this.currentY)
 this.currentY += 8

 // Bank details box
 this.doc.setFillColor(245, 245, 245)
 this.doc.roundedRect(this.margin, this.currentY - 3, 100, 40, 3, 3, 'F')

 this.doc.setFontSize(9)
 this.doc.setFont('helvetica', 'normal')
 this.doc.setTextColor(60, 60, 60)

 const startY = this.currentY
 this.doc.text(`Bank: ${bankDetails.bankName}`, this.margin + 5, startY + 4)
 this.doc.text(`Account Name: ${bankDetails.accountName}`, this.margin + 5, startY + 11)
 this.doc.text(`Account Number: ${bankDetails.accountNumber}`, this.margin + 5, startY + 18)
 this.doc.text(`Branch Code: ${bankDetails.branchCode}`, this.margin + 5, startY + 25)
 this.doc.text(`SWIFT Code: ${bankDetails.swiftCode}`, this.margin + 5, startY + 32)

 // Reference instruction
 this.doc.setFont('helvetica', 'bold')
 this.doc.setTextColor(color.r, color.g, color.b)
 this.doc.text(`Reference: ${invoice.invoice_number}`, this.margin + 115, startY + 15)

 this.currentY += 45
 }

 /**
 * Add payment terms section
 */
 private addPaymentTerms() {
 // Check if we need a new page
 if (this.currentY > this.pageHeight - 80) {
 this.doc.addPage()
 this.currentY = 20
 }

 this.doc.setFontSize(9)
 this.doc.setFont('helvetica', 'bold')
 this.doc.setTextColor(100, 100, 100)
 this.doc.text('TERMS & CONDITIONS', this.margin, this.currentY)
 this.currentY += 6

 this.doc.setFont('helvetica', 'normal')
 this.doc.setFontSize(8)
 this.doc.setTextColor(120, 120, 120)

 PAYMENT_TERMS.forEach(term => {
 this.doc.text(`• ${term}`, this.margin, this.currentY)
 this.currentY += 5
 })

 this.currentY += 10
 }

 /**
 * Add notes section
 */
 private addNotesSection(invoice: InvoiceData) {
 this.doc.setFontSize(10)
 this.doc.setFont('helvetica', 'bold')
 this.doc.setTextColor(100, 100, 100)
 this.doc.text('Notes:', this.margin, this.currentY)

 this.currentY += 6
 this.doc.setFont('helvetica', 'normal')
 this.doc.setTextColor(80, 80, 80)

 if (invoice.description) {
 const descLines = this.doc.splitTextToSize(invoice.description, this.pageWidth - 2 * this.margin)
 this.doc.text(descLines, this.margin, this.currentY)
 this.currentY += descLines.length * 5
 }

 if (invoice.notes) {
 const noteLines = this.doc.splitTextToSize(invoice.notes, this.pageWidth - 2 * this.margin)
 this.doc.text(noteLines, this.margin, this.currentY)
 this.currentY += noteLines.length * 5
 }

 this.currentY += 10
 }

 /**
 * Add footer with legal information
 */
 private addFooter(_invoice: InvoiceData) {
 const footerY = this.pageHeight - 30

 // Add horizontal line
 this.doc.setDrawColor(200, 200, 200)
 this.doc.setLineWidth(0.5)
 this.doc.line(this.margin, footerY - 10, this.pageWidth - this.margin, footerY - 10)

 // Payment instructions
 this.doc.setFontSize(8)
 this.doc.setFont('helvetica', 'normal')
 this.doc.setTextColor(100, 100, 100)
 this.doc.text('Thank you for your business. Payment is due upon receipt unless otherwise specified.', this.margin, footerY - 3)

 // Company info
 this.doc.setFontSize(7)
 this.doc.setTextColor(130, 130, 130)
 this.doc.text(
 `${this.companyDetails.name} | Reg: ${this.companyDetails.registration} | ${this.companyDetails.website}`,
 this.pageWidth / 2,
 footerY + 5,
 { align: 'center' }
 )

 // Generated timestamp
 this.doc.setFontSize(6)
 this.doc.text(
 `Generated: ${new Date().toLocaleString('en-ZA')}`,
 this.pageWidth / 2,
 footerY + 10,
 { align: 'center' }
 )
 }
}

/**
 * Utility function to generate and download invoice PDF
 */
export async function downloadInvoicePdf(invoice: InvoiceData): Promise<void> {
 const pdfService = new InvoicePdfService()
 const blob = await pdfService.generateInvoicePdf(invoice)

 // Create download link
 const url = URL.createObjectURL(blob)
 const link = document.createElement('a')
 link.href = url
 link.download = `${invoice.invoice_number}.pdf`
 document.body.appendChild(link)
 link.click()
 document.body.removeChild(link)
 URL.revokeObjectURL(url)
}

